'use es6';

import Raven from 'raven-js';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import localeComparator from 'I18n/utils/localeComparator';
import unescapedText from 'I18n/utils/unescapedText';
import awardIconUrl from 'bender-url!ecosystem-marketplace-solutions-base/img/partner-award-badge.svg';
import { CardSection } from 'foundations-components/transitional/components/Card';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { ListItem } from 'foundations-components/transitional/components/List';
import { isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { getCredentialDisplayName } from 'marketplace-ui-common/utils/credentials';
import { CERTIFICATION } from '../../constants/CredentialsConstants';
import * as ProfileKeys from '../../constants/ProfileKeys';
import { getAccreditations } from '../../selectors/accreditations';
import { getAwards, getAwardsRequestState } from '../../selectors/awards';
import { getCertifications, getCertificationsRequestState } from '../../selectors/certifications';
import { getServices } from '../../slices/serviceCatalogSlice';
import { getCredentialById } from '../../utils/credentials';
import GeneralInfoCardList from './GeneralInfoCardList';
import IconListItem from './IconListItem';
import ProfileCardWrapper from './ProfileCardWrapper';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const accreditationsDescriptionKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.accreditation.description';
const accreditationsTooltipAccessibleNameKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.accreditation.tooltipAccessibleName';
const badgesDescriptionKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.badge.description';
const badgesTooltipAccessibleNameKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.badge.tooltipAccessibleName';
const certificationsDescriptionKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.certification.description';
const certificationsTooltipAccessibleNameKey = 'ecosystem-marketplace-solutions-ui.storefront.filters.certification.tooltipAccessibleName';
const buildItemTranslationKey = (listCategoryKey, listValue, subKey) => {
  const i18nBase = listCategoryKey === 'industries' ? 'marketplace-ui-common' : 'marketplace-ui-solutions-core';
  const itemTranslationKey = `${i18nBase}.${ProfileKeys.TO_FILTER_VALUE_MAP[listCategoryKey]}.${listValue}${subKey}`;
  if (!isValidI18nKey(itemTranslationKey)) {
    Raven.captureMessage(`Invalid ${listCategoryKey} translation - ${listValue}`, {
      level: 'warning',
      extra: {
        value: listValue
      }
    });
    return null;
  }
  return itemTranslationKey;
};
const GeneralInfoCard = ({
  profileListing
}) => {
  const lang = getLang();
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const certifications = useSelector(getCertifications);
  const awardsRequestState = useSelector(getAwardsRequestState);
  const awards = useSelector(getAwards);
  const accreditations = useSelector(getAccreditations);
  const catalogServices = useSelector(getServices);
  const buildBulletedList = useCallback((listCategoryKey, subKey = '', expandable = false) => {
    const listItemsToMap = profileListing[listCategoryKey];
    if (!listItemsToMap.length) {
      return null;
    }
    let translatedItems;
    if (listCategoryKey === ProfileKeys.SERVICES) {
      translatedItems = listItemsToMap.map(listValue => {
        const catalogService = catalogServices.filter(service => service.id === listValue)[0];
        const catalogServiceName = catalogService && catalogServices.filter(service => service.id === listValue)[0].name;
        return /*#__PURE__*/_jsx(ListItem, {
          children: catalogServiceName
        }, `${catalogServiceName}-${listValue}`);
      }).sort((a, b) => localeComparator(a.key, b.key));
    } else {
      translatedItems = listItemsToMap.map(listValue => {
        const itemTranslationKey = buildItemTranslationKey(listCategoryKey, listValue, subKey);
        return itemTranslationKey ? /*#__PURE__*/_jsx(ListItem, {
          children: I18n.text(itemTranslationKey)
        }, I18n.text(itemTranslationKey)) : null;
      }).sort((a, b) => localeComparator(a, b));
    }
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: listCategoryKey,
      listPaddingInlineStart: "400",
      styled: true,
      expandable: expandable,
      children: translatedItems
    });
  }, [catalogServices, profileListing]);
  const renderedBudgetList = useMemo(() => /*#__PURE__*/_jsx(GeneralInfoCardList, {
    listCategoryKey: ProfileKeys.BUDGET,
    listPaddingInlineStart: "400",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `marketplace-ui-solutions-core.${ProfileKeys.TO_FILTER_VALUE_MAP[ProfileKeys.BUDGET]}.${profileListing[ProfileKeys.BUDGET]}.sentence`
    })
  }), [profileListing]);
  const renderedLanguageList = useMemo(() => /*#__PURE__*/_jsx(GeneralInfoCardList, {
    listCategoryKey: ProfileKeys.LANGUAGES,
    listPaddingInlineStart: "400",
    styled: true,
    children: profileListing[ProfileKeys.LANGUAGES].map(listValue => /*#__PURE__*/_jsx(ListItem, {
      children: I18n.text(`SharedI18nStrings.languageNames.${listValue}`)
    }, listValue)).sort((a, b) => localeComparator(a.key, b.key))
  }), [profileListing]);
  const renderedAccreditationsList = useMemo(() => {
    const relevantAccreditations = accreditations.filter(accreditation => profileListing[ProfileKeys.CREDENTIALS].indexOf(accreditation.id) !== -1);
    if (!relevantAccreditations.length) {
      return null;
    }
    const translatedItems = relevantAccreditations.map(accreditation => {
      const itemTranslation = getCredentialDisplayName(accreditation, lang);
      return itemTranslation ? /*#__PURE__*/_jsx(IconListItem, {
        iconUrl: accreditation.icon,
        title: itemTranslation
      }, itemTranslation) : null;
    }).filter(value => value).sort((a, b) => localeComparator(a.key, b.key));
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: "accreditations",
      toolTip: /*#__PURE__*/_jsx(HelpIcon, {
        "aria-label": unescapedText(accreditationsTooltipAccessibleNameKey),
        paddingInlineStart: "100",
        role: "tooltip",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: accreditationsDescriptionKey
        })
      }),
      children: translatedItems
    });
  }, [accreditations, lang, profileListing]);
  const renderedBadgesList = useMemo(() => {
    const badges = (profileListing === null || profileListing === void 0 ? void 0 : profileListing.badges) || [];
    if (!isSucceeded(awardsRequestState) || !badges.length) {
      return null;
    }
    const translatedItems = badges.map(listValue => {
      const badge = awardsRequestState && getCredentialById(awards, listValue);
      const itemTranslation = getCredentialDisplayName(badge, lang);
      return itemTranslation ? /*#__PURE__*/_jsx(IconListItem, {
        iconUrl: badge.icon,
        title: itemTranslation
      }, itemTranslation) : null;
    }).filter(value => value).sort((a, b) => localeComparator(a.key, b.key));
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: "badges",
      toolTip: /*#__PURE__*/_jsx(HelpIcon, {
        "aria-label": unescapedText(badgesTooltipAccessibleNameKey),
        paddingInlineStart: "100",
        role: "tooltip",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: badgesDescriptionKey
        })
      }),
      children: translatedItems
    });
  }, [awards, awardsRequestState, lang, profileListing]);
  const renderedCertificationsList = useMemo(() => {
    const listItemsToMap = profileListing[ProfileKeys.CREDENTIALS].filter(credentialId => !!getCredentialById(certifications, credentialId));
    if (!isSucceeded(certificationsRequestState) || !listItemsToMap.length) {
      return null;
    }
    const translatedCertifications = [];
    listItemsToMap.forEach(listValue => {
      const credentialItem = getCredentialById(certifications, listValue);
      if (credentialItem && credentialItem.type === CERTIFICATION) {
        const iconUrl = credentialItem.icon;
        const translation = getCredentialDisplayName(credentialItem, lang);
        if (translation) {
          translatedCertifications.push( /*#__PURE__*/_jsx(IconListItem, {
            iconUrl: iconUrl,
            title: translation
          }, `${translation}-${listValue}`));
        }
      }
    });
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      expandable: true,
      listCategoryKey: ProfileKeys.CERTIFICATIONS,
      toolTip: /*#__PURE__*/_jsx(HelpIcon, {
        "aria-label": unescapedText(certificationsTooltipAccessibleNameKey),
        paddingInlineStart: "100",
        role: "tooltip",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: certificationsDescriptionKey
        })
      }),
      children: translatedCertifications.sort((a, b) => localeComparator(a.key, b.key))
    });
  }, [certifications, certificationsRequestState, lang, profileListing]);
  const renderedAwardsList = useMemo(() => {
    const listItemsToMap = profileListing[ProfileKeys.CREDENTIALS].filter(credentialId => !!getCredentialById(awards, credentialId));
    if (!isSucceeded(awardsRequestState) || !listItemsToMap.length) {
      return null;
    }
    const translatedItems = listItemsToMap.map(listValue => {
      const award = awardsRequestState && getCredentialById(awards, listValue);
      const itemTranslation = getCredentialDisplayName(award, lang);
      return itemTranslation ? /*#__PURE__*/_jsx(IconListItem, {
        iconUrl: awardIconUrl,
        title: itemTranslation
      }, itemTranslation) : null;
    }).filter(value => value).sort((a, b) => localeComparator(a.key, b.key));
    return /*#__PURE__*/_jsx(GeneralInfoCardList, {
      listCategoryKey: ProfileKeys.AWARDS,
      children: translatedItems
    });
  }, [awards, awardsRequestState, lang, profileListing]);
  return /*#__PURE__*/_jsx(ProfileCardWrapper, {
    children: /*#__PURE__*/_jsxs(CardSection, {
      children: [buildBulletedList(ProfileKeys.SERVICES, '.sentence', true), buildBulletedList(ProfileKeys.INDUSTRIES, '.sentence'), renderedBudgetList, renderedLanguageList, buildBulletedList(ProfileKeys.REGIONS), renderedAccreditationsList, renderedBadgesList, renderedCertificationsList, renderedAwardsList]
    })
  });
};
export default GeneralInfoCard;